import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { ErrorPage } from '@/components/errors/ErrorPage';

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	eventId: string | null;
	error: Error | null;
}

/**
 * Captures React component errors and reports them to Sentry.
 * Displays a full-page error UI when errors occur.
 */
export class SentryErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, eventId: null, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, eventId: null, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const eventId = Sentry.captureException(error, {
			contexts: {
				react: {
					componentStack: errorInfo.componentStack,
				},
			},
		});
		this.setState({ eventId });
	}

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return (
				<ErrorPage
					error={this.state.error ?? undefined}
					resetErrorBoundary={() => {
						this.setState({ hasError: false, eventId: null, error: null });
						window.location.href = '/';
					}}
					eventId={this.state.eventId}
				/>
			);
		}

		return this.props.children;
	}
}
